import { motion, useReducedMotion } from 'framer-motion'
import { Star } from 'lucide-react'
import { Avatar, AvatarImage, Button } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  heroBadgeVariants,
  heroHeadlineVariants,
  heroDescriptionVariants,
  heroButtonsVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import type { HeroCenteredProps } from './types'

/**
 * HeroCentered - Simple centered layout with optional social proof
 * When reviews are provided, displays avatars and rating above or below the headline
 *
 * @remarks
 * **AI GENERATION NOTE:** Do NOT add social proof (reviews prop) unless the user
 * explicitly requests it. Social proof requires real customer data and should
 * only be added when the business has actual reviews/testimonials to display.
 */
const HeroCentered = ({
  title = 'A Collection of Components Built With MDXUI',
  subtitle = 'Beautiful, accessible components built with React, Tailwind and MDXUI. Copy and paste into your projects with confidence.',
  callToAction = 'Get Started',
  callToActionUrl = '#',
  secondaryCallToAction,
  secondaryCallToActionUrl,
  reviews,
  socialProofPosition = 'top',
  background,
  behindNavbar = true,
  className,
}: Omit<HeroCenteredProps, 'variant'>) => {
  const prefersReducedMotion = useReducedMotion()

  const badgeAnimation = prefersReducedMotion ? reducedMotionVariants : heroBadgeVariants
  const headlineAnimation = prefersReducedMotion ? reducedMotionVariants : heroHeadlineVariants
  const descAnimation = prefersReducedMotion ? reducedMotionVariants : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion ? reducedMotionVariants : heroButtonsVariants

  const SocialProof = () => {
    if (!reviews) return null
    return (
      <motion.div
        variants={badgeAnimation}
        className={socialProofPosition === 'top' ? 'mb-8' : 'mt-8'}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="flex -space-x-2">
            {reviews.avatars.slice(0, 5).map((avatar, index) => (
            <Avatar key={index.toString()} className="border-background size-10 border-2">
              <AvatarImage src={avatar.src} alt={avatar.alt} />
            </Avatar>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i.toString()} className="text-primary size-4 fill-current" />
            ))}
          </div>
          <span className="text-muted-foreground text-sm">
            from {reviews.count}+ reviews
          </span>
        </div>
      </div>
    </motion.div>
    )
  }

  return (
    <div className={cn(
      'relative overflow-hidden',
      behindNavbar && BEHIND_NAVBAR_CLASSES,
      className
    )}>
      {background && (
        <div className="absolute inset-0">{background}</div>
      )}
      <motion.section
        className="relative z-10 py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center">
          {reviews && socialProofPosition === 'top' && <SocialProof />}

          <motion.h1
            variants={headlineAnimation}
            className="mb-6 max-w-4xl text-pretty text-5xl font-semibold lg:text-6xl"
          >
            {title}
          </motion.h1>
          <motion.div
            variants={descAnimation}
            className="mb-8 max-w-2xl"
          >
            <Markdown size="lg">{subtitle}</Markdown>
          </motion.div>

          <motion.div
            variants={buttonsAnimation}
            className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center"
          >
            {callToAction && callToActionUrl && (
              <Button asChild size="lg" className="w-full sm:w-auto">
                <a href={callToActionUrl}>{callToAction}</a>
              </Button>
            )}
            {secondaryCallToAction && (
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <a href={secondaryCallToActionUrl || '#'}>{secondaryCallToAction}</a>
              </Button>
            )}
          </motion.div>

          {reviews && socialProofPosition === 'bottom' && <SocialProof />}
        </div>
      </div>
    </motion.section>
    </div>
  )
}

export { HeroCentered }
